import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardBody,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Stack,
  Text,
  VStack,
  Alert,
  AlertIcon,
  Container,
  Flex,
  Icon,
  useColorModeValue,
} from '@chakra-ui/react';
import CompassLogo from '../../assets/CompassLogo';
import { useForm } from 'react-hook-form';
import { AuthService } from '../../services/authService';
import { useAppInfoContext } from '../../contexts/AppInfoContext';

interface PasswordResetFormData {
  email: string;
}

export const PasswordReset: React.FC = () => {
  const [isSuccessful, setIsSuccessful] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<PasswordResetFormData>();

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.900', 'white');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');

  const onSubmit = async (data: PasswordResetFormData) => {
    try {
      setError(null);
      await AuthService.resetPassword(data.email);
      setIsSuccessful(true);
    } catch (err: any) {
      setError(err.message || 'Password reset failed. Please try again.');
    }
  };

  if (isSuccessful) {
    return (
      <Container maxW="lg" py={{ base: '12', md: '24' }} px={{ base: '0', sm: '8' }}>
        <Stack spacing="8">
          <Box
            py={{ base: '0', sm: '8' }}
            px={{ base: '4', sm: '10' }}
            bg={bgColor}
            boxShadow={{ base: 'none', sm: 'md' }}
            borderRadius={{ base: 'none', sm: 'xl' }}
            border="1px"
            borderColor={borderColor}
          >
            <Stack spacing="6">
              <VStack spacing="6">
                <Flex align="center" justify="center">
                  <CompassLogo boxSize={16} color={useColorModeValue('#007335', '#38A169')} />
                </Flex>
                <Stack spacing="2" textAlign="center">
                  <Heading size="lg" color={textColor}>
                    Password Reset Sent
                  </Heading>
                  <Text color={mutedColor}>
                    Check your email for password reset instructions
                  </Text>
                </Stack>
              </VStack>

              <Alert status="success" borderRadius="md">
                <AlertIcon />
                Password reset instructions have been sent to your email address.
              </Alert>

              <Button
                as={Link}
                to="/auth/login"
                colorScheme="primary"
                size="lg"
                fontSize="md"
              >
                Back to Sign In
              </Button>
            </Stack>
          </Box>
        </Stack>
      </Container>
    );
  }

  return (
    <Container maxW="lg" py={{ base: '12', md: '24' }} px={{ base: '0', sm: '8' }}>
      <Stack spacing="8">
        <Box
          py={{ base: '0', sm: '8' }}
          px={{ base: '4', sm: '10' }}
          bg={bgColor}
          boxShadow={{ base: 'none', sm: 'md' }}
          borderRadius={{ base: 'none', sm: 'xl' }}
          border="1px"
          borderColor={borderColor}
        >
          <Stack spacing="6">
            <VStack spacing="6">
              <Flex align="center" justify="center">
                <CompassLogo boxSize={16} color={useColorModeValue('#007335', '#38A169')} />
              </Flex>
              <Stack spacing="2" textAlign="center">
                <Heading size="lg" color={textColor}>
                  Reset Password
                </Heading>
                <Text color={mutedColor}>
                  Enter your email address and we'll send you instructions to reset your password
                </Text>
              </Stack>
            </VStack>

            <Box as="form" onSubmit={handleSubmit(onSubmit)}>
              <Stack spacing="6">
                <FormControl isInvalid={!!errors.email}>
                  <FormLabel htmlFor="email" color={textColor}>
                    Email Address
                  </FormLabel>
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email address"
                    {...register('email', {
                      required: 'Email address is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Please enter a valid email address',
                      },
                    })}
                  />
                  {errors.email && (
                    <Text color="red.500" fontSize="sm">
                      {errors.email.message}
                    </Text>
                  )}
                </FormControl>

                {error && (
                  <Alert status="error" borderRadius="md">
                    <AlertIcon />
                    {error}
                  </Alert>
                )}

                <Stack spacing="4">
                  <Button
                    type="submit"
                    colorScheme="primary"
                    size="lg"
                    fontSize="md"
                    isLoading={isSubmitting}
                    loadingText="Sending..."
                  >
                    Send Reset Instructions
                  </Button>

                  <Button
                    as={Link}
                    to="/auth/login"
                    variant="ghost"
                    size="lg"
                    fontSize="md"
                  >
                    Back to Sign In
                  </Button>
                </Stack>
              </Stack>
            </Box>
          </Stack>
        </Box>
      </Stack>
    </Container>
  );
};